"use client"

import { useState, useEffect } from "react"
import { LinkCreator } from "./linkCreator"
import { LinkList } from "./linkList"
import { Pagination } from "./Pagination"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/UI/tabs"
import { List, Plus, Loader2, Search, Filter, X } from "lucide-react"
import LayoutDefault from "../../layouts/LayoutDefault"
import sublinkService from "../../utils/services/sublink"
import { useIdPath } from "@/contexts/IdPathProvider"
import { useToast } from '@/templates/Admin/components/Toast'
import { Button } from "@/components/UI/button"
import { Input } from "@/components/UI/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/UI/select"


interface subLinkProps {
  onClose: () => void;
  isSidebarOpen: boolean;
}

// Interface cho dữ liệu link từ API
interface SubLink {
  id: number;
  url: string;
  inherit_mode: "include" | "exclude";
  created_by: number;
  created_at: string;
  updated_by: number;
  updated_at: string;
  meta: {
    home_view: string | null;
    title: string;
    description: string | null;
    image: string | null;
  };
  entity_counts: {
    asset?: number;
    group?: number;
    scene?: number;
    scenes?: number;
    groups?: number;
    assets?: number;
    hotspots?: number;
  };
  total_entities: number;
}

// Interface cho response chi tiết từ getSublinkDetailByID
interface SubLinkDetailResponse {
  success: boolean;
  data: {
    id: number;
    url: string;
    inherit_mode: "include" | "exclude";
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
    meta: {
      home_view: string | null;
      title: string;
      description: string | null;
      image: string | null;
    };
    entity_details: {
      scenes: Array<{
        id: number;
        name: string;
        title: string | null;
        thumbnail: string;
        is_show: number;
        view_count: number;
        information: string | null;
      }>;
      groups: Array<{
        id: number;
        title: string;
        thumbnail: string;
        is_show: number;
        information: string;
      }>;
      assets: Array<{
        id: number | null;
        title: string | null;
        size: string | null;
        information: string | null;
      }>;
      hotspots: Array<any>;
    };
    entity_counts: {
      scenes: number;
      groups: number;
      assets: number;
      hotspots: number;
    };
    total_entities: number;
  };
}

interface SublinkMeta {
  id: number;
  url: string;
  inherit_mode: "include" | "exclude";
  home_view: string | null;
  title: string;
  description: string | null;
  image: string | null;
}

// Interface cho response API phân trang
interface PaginatedResponse {
  success: boolean;
  data: SubLink[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export default function SubLink({ onClose, isSidebarOpen }: subLinkProps) {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState("list")
  const [links, setLinks] = useState<SubLink[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [perPage, setPerPage] = useState(10) // Số item per page
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<SubLink[]>([])
  const [editingLink, setEditingLink] = useState<SublinkMeta | null>(null)
  const [loadingEdit, setLoadingEdit] = useState(false)
  const { idPath } = useIdPath()

  // Fetch data từ API
  const fetchLinks = async (page: number = 1) => {
    if (!idPath) return
    
    setLoading(true)
    setError(null) // Reset error state
    try {
      const response = await sublinkService.getAllSubLinkWithDetail(idPath, perPage, page)
      
      
      if (response?.success && response?.data) {
        setLinks(response.data)
        
        // Cập nhật metadata phân trang từ pagination object
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage || page)
          setTotalPages(response.pagination.totalPages || 1)
          setTotalCount(response.pagination.totalItems || 0)
        } else {
          // Fallback nếu không có pagination
          setCurrentPage(page)
          setTotalPages(1)
          setTotalCount(response.data.length)
        }
        setError(null) // Clear any previous errors
      } else {
        // Fallback nếu response không đúng format hoặc success = false
        console.warn('Invalid response format or unsuccessful:', response)
        setLinks([])
        setCurrentPage(1)
        setTotalPages(1)
        setTotalCount(0)
        setError(response?.message || 'API returned unsuccessful response')
      }
    } catch (error) {
      console.error('Error fetching sublinks:', error)
      // Fallback to empty data
      setLinks([])
      setCurrentPage(1)
      setTotalPages(1)
      setTotalCount(0)
      setError(error instanceof Error ? error.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }

  // Fetch chi tiết link để chỉnh sửa
  const fetchLinkDetail = async (linkId: number) => {
    if (!idPath) return
    
    setLoadingEdit(true)
    setError(null)
    try {
      const response = await sublinkService.getMetaSublinkById(idPath, linkId)
      
      
      if (response?.success && response?.data) {
        setEditingLink(response.data)
        setActiveTab("create") // Chuyển đến tab tạo/sửa
        setError(null)
      } else {
        setError('Không thể tải thông tin link để chỉnh sửa')
      }
    } catch (error) {
      console.error('Error fetching link detail:', error)
      setError(error instanceof Error ? error.message : 'Unknown error occurred')
    } finally {
      setLoadingEdit(false)
    }
  }

  // Search sublinks via API
  const searchSubLinks = async (searchTerm: string) => {
    if (!idPath || !searchTerm.trim()) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    setError(null)
    
    try {
      const response = await sublinkService.searchSubLink(idPath, searchTerm.trim())

      
      if (response?.success && response?.data) {
        setSearchResults(Array.isArray(response.data) ? response.data : [])
      } else {
        console.warn('⚠️ No results found in search response')
        setSearchResults([])
      }
    } catch (error) {
      console.error('❌ Error searching sublinks:', error)
      setSearchResults([])
      setError(error instanceof Error ? error.message : 'Lỗi khi tìm kiếm')
    } finally {
      setIsSearching(false)
    }
  }

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        searchSubLinks(searchTerm)
      } else {
        setSearchResults([])
        setIsSearching(false)
      }
    }, 300) // Debounce 300ms

    return () => clearTimeout(timeoutId)
  }, [searchTerm, idPath])

  // Load data when component mounts or idPath changes
  useEffect(() => {
    if (idPath) {
      fetchLinks(1) // Reset to page 1 when idPath changes
    }
  }, [idPath])

  // Hotkeys for pagination
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if not typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      if (event.key === 'ArrowLeft' && event.ctrlKey && currentPage > 1) {
        event.preventDefault()
        handlePageChange(currentPage - 1)
      } else if (event.key === 'ArrowRight' && event.ctrlKey && currentPage < totalPages) {
        event.preventDefault()
        handlePageChange(currentPage + 1)
      } else if (event.key === 'Home' && event.ctrlKey && currentPage > 1) {
        event.preventDefault()
        handlePageChange(1)
      } else if (event.key === 'End' && event.ctrlKey && currentPage < totalPages) {
        event.preventDefault()
        handlePageChange(totalPages)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentPage, totalPages])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    fetchLinks(page)
  }

  const handleCreateLink = (linkData: any) => {
    if (editingLink) {
      // Chế độ chỉnh sửa - cập nhật link hiện có
      handleUpdateLink(editingLink.id, linkData)
    } else {
      // Chế độ tạo mới
      handleCreateNewLink(linkData)
    }
  }

  const handleCreateNewLink = async (linkData: any) => {
    try {
      
      // Prepare data for createSublinkWithMeta API
      const requestData = {
        url: linkData.url,
        inherit_mode: linkData.inherit_mode || "include",
        meta: {
          title: linkData.title,
          description: linkData.description || null,
          thumbnail: linkData.image || null,
          home_view: linkData.home_view || null,
          location: linkData?.location || null,
            opening_date: linkData?.opening_date || null,
            opening_hours: linkData?.opening_hours || null,
            lat: linkData?.lat || null,
            lng: linkData?.lng || null,
            thumbnail_mobile_app: linkData?.thumbnail_mobile_app || null,
        },
        entities: {
          scene: linkData.selectedObjects?.scenes?.map(id => parseInt(id)).filter(id => !isNaN(id)) || [],
          group: linkData.selectedObjects?.groups?.map(id => parseInt(id)).filter(id => !isNaN(id)) || [],
          hotspot: linkData.selectedObjects?.hotspots?.map(id => parseInt(id)).filter(id => !isNaN(id)) || [],
          // Handle assets with both numeric IDs and string UUIDs
          asset: linkData.selectedObjects?.assets?.map(id => {
            const numId = parseInt(id)
            return !isNaN(numId) ? numId : id
          }) || []
        }
      }
     
      const response = await sublinkService.createSublinkWithMeta(idPath, requestData)
    
      if (response?.success) {
        // Success - refresh the list to show new sublink
        showSuccess('Tạo sublink thành công!')
        
        // Switch to list tab and refresh data
        setActiveTab("list")
        setEditingLink(null) // Reset editing state
        fetchLinks(currentPage) // Refresh the list
        
      } else {
        throw new Error(response?.message || 'API call failed')
      }
      
    } catch (error) {
      console.error('❌ Error creating sublink:', error)
      showError(`Lỗi khi tạo sublink: ${error.message}`)
    }
  }

  const handleUpdateLink = async (linkId: number, linkData: any) => {
    try {
   
      // Update local state temporarily
      setLinks(links.map(link => 
        link.id === linkId 
          ? {
              ...link,
              url: linkData.url,
              inherit_mode: linkData.inherit_mode || "include",
              updated_at: new Date().toISOString(),
              meta: {
                ...link.meta,
                title: linkData.title,
                description: linkData.description || null,
                image: linkData.image || null,
                home_view: linkData.home_view || null,
              }
            }
          : link
      ))
      
      setActiveTab("list")
      setEditingLink(null) // Reset editing state
      
      // Only refresh data after actual update completion
      fetchLinks(currentPage)
    } catch (error) {
      console.error('Error updating link:', error)
      setError(error instanceof Error ? error.message : 'Unknown error occurred')
    }
  }

  const handleDeleteLink = async (id: number) => {
    if (!idPath) {
      setError('ID Path không có sẵn')
      return
    }

    // Find the link to show in confirmation
    const linkToDelete = links.find(link => link.id === id)
    const linkTitle = linkToDelete?.meta?.title || linkToDelete?.url || `Link #${id}`
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Bạn có chắc chắn muốn xóa link "${linkTitle}"?\n\nHành động này không thể hoàn tác.`
    )
    
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      
      // Call API to delete sublink
      const response = await sublinkService.deleteSubLink(idPath, id)
      
      if (response?.success || !response?.error) {
        
        // Update local state immediately for better UX
        setLinks(links.filter((link) => link.id !== id))
        
        // Refresh data to ensure consistency
        await fetchLinks(currentPage)
        
      } else {
        throw new Error(response?.message || 'Không thể xóa link')
      }
    } catch (error) {
      console.error('Error deleting link:', error)
      setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi xóa link')
    } finally {
      setLoading(false)
    }
  }

  const handleEditLink = (id: number) => {
    fetchLinkDetail(id)
  }

  const handleCancelEdit = () => {
    setEditingLink(null)
    setActiveTab("list")
  }

  const handleTabChange = (value: string) => {
    if (value === "list" && editingLink) {
      // Reset editing state when switching to list
      setEditingLink(null)
    }
    setActiveTab(value)
  }

  const handleRefresh = () => {
    // Clear search when refreshing to show all data
    setSearchTerm("")
    setSearchResults([])
    setIsSearching(false)
    fetchLinks(currentPage)
  }

  // Get data to display: search results if searching, otherwise normal pagination data
  const displayData = searchTerm.trim() ? searchResults : links
  const isShowingSearchResults = searchTerm.trim() && searchResults.length >= 0

  // Handle items per page change
  const handlePerPageChange = (value: string) => {
    const newPerPage = parseInt(value)
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page
    // API sẽ tự động sử dụng newPerPage trong lần fetch tiếp theo
    fetchLinks(1) // Fetch with new per page setting
  }

  return (
   <LayoutDefault isSidebarOpen={isSidebarOpen} onClose={onClose}>
    <main className="min-h-screen p-6 md:p-12">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Quản lý liên kết con (Sublink)</h1>
            <p className="text-muted-foreground">
              Tạo và quản lý các liên kết con với các đối tượng được chọn
              {totalCount > 0 && (
                <span className="ml-2 text-sm">
                  (Tổng: {totalCount} links)
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <List className="w-4 h-4" />
              )}
              Làm mới
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              size="icon"
              className="flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm theo tiêu đề, URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              {/* Search loading indicator */}
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                </div>
              )}
            </div>
            {/* Search status info */}
            {searchTerm.trim() && (
              <div className="mt-2 text-sm text-muted-foreground">
                {isSearching ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Đang tìm kiếm...
                  </span>
                ) : (
                  <span>
                    Tìm thấy {searchResults.length} kết quả cho "{searchTerm}"
                    {searchResults.length === 0 && (
                      <span className="text-amber-600"> - Không có kết quả</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Hiển thị:</span>
            <Select value={perPage.toString()} onValueChange={handlePerPageChange}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Lọc
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Danh sách liên kết con
            </TabsTrigger>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {editingLink ? 'Chỉnh sửa liên kết' : 'Tạo liên kết con'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list">
            {(loading || isSearching) ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">
                  {isSearching ? 'Đang tìm kiếm...' : 'Đang tải...'}
                </span>
              </div>
            ) : (
              <>
                {/* Error state */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-red-700 text-sm">
                      <strong>Lỗi:</strong> {error}
                    </div>
                  </div>
                )}
                
                {/* Search results info */}
                {isShowingSearchResults && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-700">
                        Kết quả tìm kiếm cho <strong>"{searchTerm}"</strong>: 
                        {searchResults.length > 0 ? (
                          <span className="ml-1">{searchResults.length} links được tìm thấy</span>
                        ) : (
                          <span className="ml-1 text-amber-600">Không có kết quả</span>
                        )}
                      </span>
                      <button
                        onClick={() => {
                          setSearchTerm("")
                          setSearchResults([])
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                      >
                        Xóa tìm kiếm
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Debug info */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mb-4 p-3 bg-gray-100 rounded text-xs">
                    <strong>Debug:</strong> 
                    {isShowingSearchResults ? (
                      <>Search Mode | Query: "{searchTerm}" | Results: {searchResults.length}</>
                    ) : (
                      <>Page {currentPage}/{totalPages} | Total: {totalCount} | Per page: {perPage} | Links: {links.length}</>
                    )}
                    <br />
                    <strong>ID Path:</strong> {idPath || 'Not set'}
                    <br />
                    <strong>Search State:</strong> isSearching: {isSearching}, searchTerm: "{searchTerm}", results: {searchResults.length}
                    <br />
                    <strong>Edit Mode:</strong> {editingLink ? `Editing ID ${editingLink.id}` : 'Not editing'} | Loading: {loadingEdit}
                  </div>
                )}
                
                {/* Normal pagination info - only show when not searching */}
                {!isShowingSearchResults && totalCount > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-700">
                        Đang hiển thị {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalCount)} 
                        trong tổng số <strong>{totalCount}</strong> links
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="text-blue-600">
                          Trang {currentPage} / {totalPages}
                        </span>
                        <div className="text-xs text-blue-500">
                          Ctrl + ← → để chuyển trang | Ctrl + Home/End
                        </div>
                      </div>
                    </div>
                    
                    {/* Additional API info */}
                    <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-600">
                      API Status: ✅ Connected | Response Format: success + data + pagination
                    </div>
                  </div>
                )}
                
                <LinkList 
                  links={displayData} 
                  onDelete={handleDeleteLink} 
                  onEdit={handleEditLink}
                />
                
                {/* Pagination Component - only show when not searching */}
                {!isShowingSearchResults && totalCount > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    perPage={perPage}
                    onPageChange={handlePageChange}
                    loading={loading}
                  />
                )}
                
                {/* Empty state */}
                {!loading && !isSearching && displayData.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">
                      {isShowingSearchResults 
                        ? `Không tìm thấy kết quả nào cho "${searchTerm}"` 
                        : 'Không có link nào được tìm thấy'
                      }
                    </p>
                    {!isShowingSearchResults && (
                      <Button 
                        onClick={() => {
                          setEditingLink(null) // Make sure we're not in edit mode
                          setActiveTab("create")
                        }} 
                        className="mt-4"
                      >
                        Tạo link đầu tiên
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="create">
            {loadingEdit ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Đang tải dữ liệu...</span>
              </div>
            ) : (
              <LinkCreator 
                onSubmit={handleCreateLink}
                editingData={editingLink}
                onCancel={handleCancelEdit}
                isEditing={!!editingLink}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  </LayoutDefault>
  )
}
